/**
 * @voltxampere/schema — .vxa manifest schema
 *
 * Per SPECS §2.4. The manifest lives at `manifest.json` inside the ZIP
 * and is the first file read during the `.vxa` open sequence (SPECS
 * §2.5). Its `checksum` field is validated against the SHA-256 of the
 * raw `circuit.json` bytes before the circuit is parsed.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
import { SemverSchema, UUIDSchema } from "../schemas/_primitives.js";
import { CHECKSUM_PREFIX } from "./constants.js";
/**
 * ISO-8601 timestamp with support for timezone designators. Matches
 * both `Z` and `±HH:MM` forms.
 */
const ISO8601Schema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/, "must be ISO-8601 with timezone");
/**
 * `sha256-<64 lowercase hex chars>` per SPECS §2.4's checksum example.
 */
const Sha256PrefixedSchema = z
    .string()
    .regex(new RegExp(`^${CHECKSUM_PREFIX}[0-9a-f]{64}$`), `must be "${CHECKSUM_PREFIX}<64-char hex>"`);
/**
 * One embedded asset reference inside the manifest. Writers list every
 * non-JSON file the circuit depends on (images for annotations, model
 * files, etc.) with its hash + declared MIME type.
 */
export const VxaAssetReferenceSchema = z
    .object({
    path: z.string().min(1),
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, "must be 64-char lowercase hex")
        .optional(),
    type: z.string().min(1).optional(),
    size: z.number().int().nonnegative().optional(),
})
    .strict();
/**
 * Declared prerequisites. Readers use this to prompt the user if a
 * required component or plugin is missing (SPECS §2.5 step 6).
 */
export const VxaRequiresSchema = z
    .object({
    components: z.array(z.string()).default([]),
    plugins: z.array(z.string()).default([]),
    models: z.array(z.string()).default([]),
})
    .strict()
    .default({});
export const VxaManifestSchema = z
    .object({
    $schema: z.string().url().optional(),
    vxaVersion: SemverSchema,
    formatVersion: SemverSchema,
    circuitId: UUIDSchema,
    name: z.string().min(1).max(200),
    author: z.string().max(200).optional(),
    license: z.string().max(100).optional(),
    createdAt: ISO8601Schema,
    updatedAt: ISO8601Schema,
    checksum: Sha256PrefixedSchema,
    assets: z.array(VxaAssetReferenceSchema).default([]),
    requires: VxaRequiresSchema,
})
    .strict();
//# sourceMappingURL=manifest-schema.js.map